"use client"
import { useEffect, useState } from "react"
import { Video } from "../Video/Video"
import { Icon } from "@/components/formatters/MXPIcon"
import { renderContentfulImage } from "../../utils/renderImage"
import { IMAGE_QUALITY_MOBILE } from "@/lib/heroImageUtils"

export interface VideoCovered {
  image?: {
    url: string
    alt: string
    focusArea?: string
  }
  videoId?: string
  isNoDescription?: boolean
  isActiveSlide?: boolean
  fullBackgroundVideo?: boolean
  forceFullHeightVideo?: boolean
  /** Thumbnail layout width per breakpoint. Defaults to this component's own
   *  max-w-[1060px] wrapper; pass a narrower value when a parent caps it. */
  sizes?: string
}

export const VideoCovered = ({
  image,
  videoId,
  isNoDescription,
  isActiveSlide,
  fullBackgroundVideo = false,
  forceFullHeightVideo = false,
  sizes = "(min-width: 1060px) 1060px, 100vw",
}: VideoCovered) => {
  const [showVideo, setShowVideo] = useState(false)
  const [videoKey, setVideoKey] = useState(0)

  useEffect(() => {
    if (isActiveSlide === false) {
      setShowVideo(false)
      setVideoKey(k => k + 1)
    }
  }, [isActiveSlide])

  // Compute at render time so the iframe is removed in the SAME render
  // that receives isActiveSlide=false — no one-frame delay.
  const shouldShowVideo = showVideo && isActiveSlide !== false

  if (fullBackgroundVideo) {
    return (
      <div
        className={
          forceFullHeightVideo
            ? "absolute inset-0 h-full w-full overflow-hidden"
            : "relative w-full overflow-hidden"
        }
      >
        <Video
          key={videoKey}
          autoplay={true}
          videoId={videoId}
          isNoDescription={isNoDescription}
          fullBackgroundVideo={true}
          forceFullHeightVideo={forceFullHeightVideo}
        />
      </div>
    )
  }
  if (!image || image.url === undefined || image.url === null) {
    return (
      <div className="max-w-[1060px] mx-auto aspect-video [&_iframe]:w-full [&_iframe]:h-full">
        {isActiveSlide !== false ? (
          <Video
            key={videoKey}
            autoplay={false}
            videoId={videoId}
            isNoDescription={isNoDescription}
          />
        ) : (
          <div className="w-full h-full bg-black" />
        )}
      </div>
    )
  }

  return (
    <div className="max-w-[1060px] mx-auto relative">
      {!shouldShowVideo ? (
        <button
          type="button"
          className="group block relative w-full aspect-video overflow-hidden p-0 border-none rounded bg-transparent cursor-pointer leading-[0]"
          onClick={() => setShowVideo(true)}
          aria-label="Play video"
        >
          {renderContentfulImage(
            image.url,
            image.alt,
            1060,
            596,
            "w-full h-full object-cover",
            IMAGE_QUALITY_MOBILE,
            image.focusArea,
            // componentImagePriority and isLCP keep their defaults; both are
            // positional, so they have to be named to reach `sizes`.
            undefined,
            false,
            sizes,
          )}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] w-16 h-16 rounded-full bg-yellow-m10 flex items-center justify-center transition-[transform,box-shadow] duration-200 ease-in-out group-hover:scale-110 group-hover:shadow-[0_4px_16px_rgb(0_0_0/0.25)] group-focus-visible:scale-110 group-focus-visible:shadow-[0_4px_16px_rgb(0_0_0/0.25)]">
            <Icon
              type="t2u-play"
              size={24}
              className="text-white translate-x-[2px]"
            />
          </div>
        </button>
      ) : (
        <div className="w-full aspect-video [&_iframe]:w-full [&_iframe]:h-full">
          <Video
            key={videoKey}
            autoplay={true}
            videoId={videoId}
            isNoDescription={isNoDescription}
          />
        </div>
      )}
    </div>
  )
}
